/**
 * The winding road of lesson medallions: a vertical snake on phones and a horizontal
 * wave on desktop. Strokes are drawn in a stretched SVG with non-scaling strokes so the
 * road keeps a constant 16px width at any container size.
 */
import type { LessonMeta } from '../data/api.ts';
import type { PathStatus } from '../core/path.ts';
import { Medallion } from './Medallion.tsx';
import './lesson-path.css';

export interface PathItem {
  lesson: LessonMeta;
  status: PathStatus;
}

const SNAKE_ROW = 82;
const SNAKE_X = [22, 78];
const WAVE_Y = [38, 70];

function roadPath(points: readonly { x: number; y: number }[], vertical: boolean): string {
  if (points.length === 0) return '';
  let d = `M${points[0]!.x} ${points[0]!.y}`;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1]!;
    const b = points[i]!;
    d += vertical
      ? ` C${a.x} ${(a.y + b.y) / 2} ${b.x} ${(a.y + b.y) / 2} ${b.x} ${b.y}`
      : ` C${(a.x + b.x) / 2} ${a.y} ${(a.x + b.x) / 2} ${b.y} ${b.x} ${b.y}`;
  }
  return d;
}

function statusText(item: PathItem): string {
  if (item.status === 'done') return 'пройден';
  if (item.status === 'current') return 'сейчас';
  if (item.status === 'open') return 'открыт';
  return item.lesson.available ? 'впереди' : 'скоро';
}

export function lessonChar(lesson: LessonMeta): string {
  return lesson.key_char ?? Array.from(lesson.title)[0] ?? '·';
}

export function LessonPath({
  items,
  layout,
  onOpen,
}: {
  items: readonly PathItem[];
  layout: 'snake' | 'wave';
  onOpen: (lessonId: string) => void;
}) {
  const vertical = layout === 'snake';
  const height = vertical ? items.length * SNAKE_ROW : 150;
  const points = items.map((_, i) =>
    vertical
      ? { x: SNAKE_X[i % 2]!, y: SNAKE_ROW / 2 + i * SNAKE_ROW }
      : { x: items.length === 1 ? 50 : 8 + (84 * i) / (items.length - 1), y: WAVE_Y[i % 2]! },
  );
  const lastDone = items.reduce((last, item, i) => (item.status !== 'locked' ? i : last), -1);
  const viewW = 100;

  return (
    <div className={`lesson-path lesson-path--${layout}`} style={{ height }}>
      <svg className="lesson-path__road" viewBox={`0 0 ${viewW} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <path d={roadPath(points, vertical)} className="lesson-path__ribbon" vectorEffect="non-scaling-stroke" />
        {lastDone > 0 && (
          <path
            d={roadPath(points.slice(0, lastDone + 1), vertical)}
            className="lesson-path__ribbon lesson-path__ribbon--done"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <path d={roadPath(points, vertical)} className="lesson-path__marking" vectorEffect="non-scaling-stroke" />
      </svg>
      <ol className="lesson-path__list">
        {items.map((item, i) => {
          const p = points[i]!;
          const open = item.status !== 'locked';
          const side = vertical ? (i % 2 === 0 ? 'right' : 'left') : 'below';
          const label = `Урок ${item.lesson.number} · ${statusText(item)}`;
          return (
            <li
              key={item.lesson.id}
              className={`lesson-path__stop lesson-path__stop--${side} lesson-path__stop--${item.status}`}
              style={{ left: `${p.x}%`, top: p.y }}
            >
              <button
                type="button"
                className="lesson-path__medallion"
                disabled={!open}
                onClick={() => onOpen(item.lesson.id)}
                aria-label={`${item.lesson.title}, ${label}`}
              >
                <Medallion char={lessonChar(item.lesson)} state={item.status} size={vertical ? 56 : 58} />
              </button>
              <span className="lesson-path__label" aria-hidden="true">
                <span className="lesson-path__title han">{item.lesson.title}</span>
                <span className="lesson-path__status">{label}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
